/**
 * Content-address tags: object carries an address; consumers store the tag string.
 * Tenant: my:… · Platform (reserved): cofw:… · One tag → one object per scope.
 *
 * Format: `prefix:name` or `prefix:name:index` (e.g. my:about, my:block1, my:blog:2).
 */

export const TENANT_CONTENT_TAG_PREFIX = 'my:';
export const PLATFORM_CONTENT_TAG_PREFIX = 'cofw:';

/** Max length including prefix (my: + slug). */
export const CONTENT_TAG_MAX_LENGTH = 80;

/** Segments after prefix: name or name:index… (lowercase, digits, - _). */
const TENANT_TAG_RE = /^my:[a-z0-9][a-z0-9_-]*(?::[a-z0-9][a-z0-9_-]*)*$/;
const PLATFORM_TAG_RE = /^cofw:[a-z0-9][a-z0-9_-]*(?::[a-z0-9][a-z0-9_-]*)*$/;

const SITE_BLOCK_TAG_RE = /^my:(block[1-5])(?::[a-z0-9][a-z0-9_-]*)*$/;

/** my:blog:<slug> puts a note on the storefront blog; the slug is its address. Also used as a SQL regex. */
export const BLOG_NOTE_TAG_PATTERN = '^my:blog:[a-z0-9][a-z0-9_-]*$';
const BLOG_NOTE_TAG_RE = new RegExp(BLOG_NOTE_TAG_PATTERN);
export const BLOG_SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;

/**
 * @param {unknown} raw
 * @returns {string} slug for my:blog:<slug>, else ''
 */
export function blogSlugFromContentTag(raw) {
  const tag = normalizeContentTagInput(raw);
  return BLOG_NOTE_TAG_RE.test(tag) ? tag.slice('my:blog:'.length) : '';
}

/**
 * @param {unknown} raw
 * @returns {string} trimmed lowercase or ''
 */
export function normalizeContentTagInput(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .slice(0, CONTENT_TAG_MAX_LENGTH);
}

/**
 * Default address for a storefront Main slot (one note per place).
 * @param {string} block e.g. block1
 * @returns {string} my:block1 or ''
 */
export function contentTagForSiteBlock(block) {
  const id = String(block || '').trim().toLowerCase();
  if (!/^block[1-5]$/.test(id)) return '';
  return `my:${id}`;
}

/**
 * If tag is my:blockN or my:blockN:index → blockN, else null.
 * @param {unknown} raw
 * @returns {'block1'|'block2'|'block3'|'block4'|'block5'|null}
 */
export function siteBlockFromContentTag(raw) {
  const tag = normalizeContentTagInput(raw);
  const match = SITE_BLOCK_TAG_RE.exec(tag);
  return match ? /** @type {'block1'|'block2'|'block3'|'block4'|'block5'} */ (match[1]) : null;
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isTenantContentTag(raw) {
  const tag = normalizeContentTagInput(raw);
  return TENANT_TAG_RE.test(tag);
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isPlatformContentTag(raw) {
  const tag = normalizeContentTagInput(raw);
  return PLATFORM_TAG_RE.test(tag);
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isContentTag(raw) {
  return isTenantContentTag(raw) || isPlatformContentTag(raw);
}

/**
 * Tenant notes may only claim my: tags. Empty clears the tag.
 * @param {unknown} raw
 * @returns {{ tag: string|null, error?: string }}
 */
export function parseTenantContentTagForAssign(raw) {
  const tag = normalizeContentTagInput(raw);
  if (!tag) {
    return { tag: null };
  }
  if (isPlatformContentTag(tag)) {
    return { tag: null, error: 'content_tag_platform_reserved' };
  }
  if (!isTenantContentTag(tag)) {
    return { tag: null, error: 'content_tag_invalid' };
  }
  return { tag };
}

/**
 * Consumer config may reference my: or cofw: (platform promo later). Invalid → empty (silence).
 * @param {unknown} raw
 * @returns {string} valid tag or ''
 */
export function normalizeContentTagRef(raw) {
  const tag = normalizeContentTagInput(raw);
  if (!tag) return '';
  if (isContentTag(tag)) return tag;
  return '';
}

/**
 * Free-form note tags must not steal the reserved prefixes.
 * @param {string} tag
 * @returns {boolean}
 */
export function isReservedContentTagPrefix(tag) {
  const t = String(tag || '').trim().toLowerCase();
  return t.startsWith(TENANT_CONTENT_TAG_PREFIX) || t.startsWith(PLATFORM_CONTENT_TAG_PREFIX);
}

/** Stable DOM id for a note rendered on the storefront by content tag. */
export function contentTagDomId(raw) {
  const tag = normalizeContentTagInput(raw);
  if (!tag) return '';
  return `website-note-${tag.replace(/^my:/, '').replace(/:/g, '-')}`;
}
